import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { EventData, PredictionData, FightPrediction, PredictionHistoryData } from '../types';
import { 
  fetchIntegratedFightData, 
  fetchPredictionInputData, 
  transformToPredictionView, 
  triggerScraperRefresh,
  fetchAllFightPredictions,
  refreshPredictions,
  checkApiHealth,
  clearRequestHeaders,
  OFV_API_URL,
  fetchMostRecentUpcomingEvent
} from '../services/dataService';
import { 
  getPredictionHistory, 
  recordEventPredictions, 
  findEventsNeedingVerification,
  reloadPredictionHistory,
  resetToMostRecentEvent,
  clearPredictionHistory,
  completeReset
} from '../services/predictionTrackingService';
import { format, add, parseISO, isBefore, parse } from 'date-fns';

interface FightDataContextType {
  isLoading: boolean;
  isRefreshing: boolean;
  refreshStatus: string | null;
  error: Error | null;
  eventName: string;
  fightPredictions: FightPrediction[];
  selectedFightIndex: number;
  predictionHistory: PredictionHistoryData;
  eventsNeedingVerification: number;
  setSelectedFightIndex: (index: number) => void;
  refreshData: () => Promise<void>;
  setPredictionHistory: (history: PredictionHistoryData) => void;
  forcePredictionHistoryRefresh: () => void;
  isToastVisible: boolean;
  toastProgress: number;
  toastMessage: string;
  dismissToast: () => void;
}

// Define interface for stat objects
interface StatObject {
  name: string;
  fighter1_value: any;
  fighter2_value: any;
  advantage?: string;
}

const FightDataContext = createContext<FightDataContextType | undefined>(undefined);

interface FightDataProviderProps {
  children: ReactNode;
}

export const FightDataProvider: React.FC<FightDataProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [refreshStatus, setRefreshStatus] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [eventName, setEventName] = useState<string>('');
  const [fightPredictions, setFightPredictions] = useState<FightPrediction[]>([]);
  const [selectedFightIndex, setSelectedFightIndex] = useState<number>(0);
  const [predictionHistory, setPredictionHistory] = useState<PredictionHistoryData>(getPredictionHistory());
  const [eventsNeedingVerification, setEventsNeedingVerification] = useState<number>(0);
  
  // Add new toast-related state
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [toastProgress, setToastProgress] = useState(0);
  const [toastMessage, setToastMessage] = useState('');

  const dismissToast = useCallback(() => {
    setIsToastVisible(false);
    setToastProgress(0);
    setToastMessage('');
  }, []);

  // Helper to record an event for prediction tracking
  const recordEventForTracking = (apiData: any, fights: FightPrediction[]) => {
    console.log("Recording event for tracking:", apiData.event_name);
    
    // Extract date from prediction filename or use current date
    let eventDate = format(new Date(), 'yyyy-MM-dd'); // Default to today
    
    // Try to extract the date from prediction filename if available
    if (apiData.prediction_file) {
      const dateMatch = apiData.prediction_file.match(/(\d{8})_/);
      if (dateMatch && dateMatch[1]) {
        const year = dateMatch[1].substring(0, 4);
        const month = dateMatch[1].substring(4, 6);
        const day = dateMatch[1].substring(6, 8);
        eventDate = `${year}-${month}-${day}`;
      }
    }
    
    // Determine if this event is in the past or future - fully dynamic approach
    const eventDateObj = parseISO(eventDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    // Event is completed if it's before today
    const isEventInPast = isBefore(eventDateObj, today);
    
    // Set status based on date comparison only - no hardcoded event names
    const status = isEventInPast ? 'completed' : 'pending';
    
    console.log(`Recording event ${apiData.event_name} with date ${eventDate} as ${status}`);
    
    // Record event with appropriate status
    recordEventPredictions(
      apiData.event_name,
      eventDate,
      fights,
      undefined,
      undefined,
      status
    );
    
    // Refresh prediction history to reflect changes
    setTimeout(() => {
      const refreshedHistory = reloadPredictionHistory();
      setPredictionHistory(refreshedHistory);
    }, 100);
  };

  // Helper specifically for completed events
  const recordCompletedEventForTracking = (apiData: any, fights: FightPrediction[]) => {
    console.log("Recording completed event for tracking:", apiData.event_name);
    
    // Extract date from prediction filename or use a reasonably past date
    let eventDate = format(add(new Date(), { days: -1 }), 'yyyy-MM-dd'); // Default to yesterday
    
    // Try to extract the date from prediction filename if available
    if (apiData.prediction_file) {
      const dateMatch = apiData.prediction_file.match(/(\d{8})_/);
      if (dateMatch && dateMatch[1]) {
        const year = dateMatch[1].substring(0, 4);
        const month = dateMatch[1].substring(4, 6);
        const day = dateMatch[1].substring(6, 8);
        eventDate = `${year}-${month}-${day}`;
      }
    }
    
    console.log(`Recording past event ${apiData.event_name} with date ${eventDate} as completed`);
    
    // Record event with completed status
    recordEventPredictions(
      apiData.event_name,
      eventDate,
      fights,
      undefined,
      undefined,
      'completed' // Always completed for this method
    );
    
    // Force a history refresh to reflect the changes
    setTimeout(() => {
      const refreshedHistory = reloadPredictionHistory();
      setPredictionHistory(refreshedHistory);
    }, 100);
  };
  
  // Function to convert API fights to FightPrediction format
  const convertApiDataToFightPredictions = (apiData: any): FightPrediction[] => {
    console.log("Converting API data to FightPrediction format", apiData);
    if (!apiData || !apiData.fights || !Array.isArray(apiData.fights)) {
      console.error("Invalid API data structure", apiData);
      return [];
    }
    
    return apiData.fights.map((fight: any) => {
      // Extract fighter 1 and fighter 2 data
      const fighter1 = fight.fighter1 || {};
      const fighter2 = fight.fighter2 || {};
      
      // Log data for debugging
      if (!fighter1.name || !fighter2.name) {
        console.warn("Missing fighter name data:", { fight_number: fight.fight_number, fighter1, fighter2 });
      }
      
      // Convert win probabilities with strong fallbacks
      const convertWinProbability = (value: any): number => {
        if (value === undefined || value === null) return 50;
        // If value is already between 0 and 1, return as is
        if (value >= 0 && value <= 1) return value * 100;
        // If value is between 0 and 100, return as is
        if (value >= 0 && value <= 100) return value;
        // Default fallback
        return 50;
      };

      const fighter1WinProbability = convertWinProbability(
        fighter1.win_probability !== undefined && fighter1.win_probability !== null
          ? fighter1.win_probability 
          : (fight.confidence && fight.predicted_winner === fighter1.name) 
            ? fight.confidence 
            : (fight.confidence && fight.predicted_winner !== fighter1.name) 
              ? 100 - (fight.confidence || 50) 
              : 50
      );
      
      const fighter2WinProbability = convertWinProbability(
        fighter2.win_probability !== undefined && fighter2.win_probability !== null
          ? fighter2.win_probability 
          : (fight.confidence && fight.predicted_winner === fighter2.name) 
            ? fight.confidence 
            : (fight.confidence && fight.predicted_winner !== fighter2.name) 
              ? 100 - (fight.confidence || 50) 
              : 50
      );
      
      // Fighter records (directly from the API)
      const fighter1Record = fighter1.record || "0-0-0";
      const fighter2Record = fighter2.record || "0-0-0";
      
      // Important stats (directly from the API's important_stats field)
      let importantStats = fight.important_stats || [];
      
      // Format stats into the frontend's expected format
      importantStats = importantStats.map((stat: StatObject) => ({
        name: stat.name,
        fighter1Value: stat.fighter1_value,
        fighter2Value: stat.fighter2_value,
        advantage: stat.advantage,
        unit: stat.name.toLowerCase().includes("per minute") ? "per min" : 
              stat.name.toLowerCase().includes("accuracy") || 
              stat.name.toLowerCase().includes("defense") ? "%" : ""
      }));
      
      // If we have stats_comparison data, add those stats
      if (fight.stats_comparison) {
        const statsComp = fight.stats_comparison;
        
        // Process striking metrics
        if (statsComp.striking_metrics && Array.isArray(statsComp.striking_metrics)) {
          statsComp.striking_metrics.forEach((metric: string) => {
            // Parse metric strings like "Significant Strikes Landed Per Minute: Fighter1 (4.53) vs Fighter2 (3.21)"
            const match = metric.match(/([^:]+):\s*[^(]+ \(([^)]+)\)\s*vs\s*[^(]+ \(([^)]+)\)/i);
            if (match) {
              const statName = match[1].trim();
              const fighter1Value = parseFloat(match[2]);
              const fighter2Value = parseFloat(match[3]);
              
              if (!isNaN(fighter1Value) && !isNaN(fighter2Value)) {
                importantStats.push({
                  name: statName,
                  fighter1Value: fighter1Value,
                  fighter2Value: fighter2Value,
                  advantage: fighter1Value > fighter2Value ? "fighter1" : "fighter2",
                  unit: statName.toLowerCase().includes("per minute") ? "per min" : 
                        statName.toLowerCase().includes("accuracy") || 
                        statName.toLowerCase().includes("defense") ? "%" : ""
                });
              }
            }
          });
        }
        
        // Process grappling metrics
        if (statsComp.grappling_metrics && Array.isArray(statsComp.grappling_metrics)) {
          statsComp.grappling_metrics.forEach((metric: string) => {
            const match = metric.match(/([^:]+):\s*[^(]+ \(([^)]+)\)\s*vs\s*[^(]+ \(([^)]+)\)/i);
            if (match) {
              const statName = match[1].trim();
              const fighter1Value = parseFloat(match[2].replace('%', ''));
              const fighter2Value = parseFloat(match[3].replace('%', ''));
              
              if (!isNaN(fighter1Value) && !isNaN(fighter2Value)) {
                importantStats.push({
                  name: statName,
                  fighter1Value: fighter1Value,
                  fighter2Value: fighter2Value,
                  advantage: fighter1Value > fighter2Value ? "fighter1" : "fighter2",
                  unit: "%"
                });
              }
            }
          });
        }
      }
      
      // Extract key stats directly from fighter stats for display
      const extractDirectStats = (fighterStats: any, fighterName: string) => {
        const stats = fighterStats || {};
        const directStats = [];
        
        // Helper function to round percentages properly
        const roundPercentage = (value: number): number => {
          return Math.round(value * 100 * 100) / 100; // Round to 2 decimal places then remove trailing zeros
        };
        
        // Significant Strikes Landed Per Minute
        if (fighter1.stats?.sigStrikesLandedPerMin !== undefined && fighter2.stats?.sigStrikesLandedPerMin !== undefined) {
          directStats.push({
            name: "Significant Strikes Landed Per Minute",
            fighter1Value: fighter1.stats.sigStrikesLandedPerMin,
            fighter2Value: fighter2.stats.sigStrikesLandedPerMin,
            advantage: fighter1.stats.sigStrikesLandedPerMin > fighter2.stats.sigStrikesLandedPerMin ? "fighter1" : "fighter2",
            unit: "per min"
          });
        }
        
        // Significant Strikes Absorbed Per Minute
        if (fighter1.stats?.sigStrikesAbsorbedPerMin !== undefined && fighter2.stats?.sigStrikesAbsorbedPerMin !== undefined) {
          directStats.push({
            name: "Significant Strikes Absorbed Per Minute",
            fighter1Value: fighter1.stats.sigStrikesAbsorbedPerMin,
            fighter2Value: fighter2.stats.sigStrikesAbsorbedPerMin,
            advantage: fighter1.stats.sigStrikesAbsorbedPerMin < fighter2.stats.sigStrikesAbsorbedPerMin ? "fighter1" : "fighter2", // Lower is better
            unit: "per min"
          });
        }
        
        // Takedown Accuracy
        if (fighter1.stats?.takedownAccuracy !== undefined && fighter2.stats?.takedownAccuracy !== undefined) {
          directStats.push({
            name: "Takedown Accuracy",
            fighter1Value: roundPercentage(fighter1.stats.takedownAccuracy),
            fighter2Value: roundPercentage(fighter2.stats.takedownAccuracy),
            advantage: fighter1.stats.takedownAccuracy > fighter2.stats.takedownAccuracy ? "fighter1" : "fighter2",
            unit: "%"
          });
        }
        
        // Striking Accuracy
        if (fighter1.stats?.strikingAccuracy !== undefined && fighter2.stats?.strikingAccuracy !== undefined) {
          directStats.push({
            name: "Striking Accuracy",
            fighter1Value: roundPercentage(fighter1.stats.strikingAccuracy),
            fighter2Value: roundPercentage(fighter2.stats.strikingAccuracy),
            advantage: fighter1.stats.strikingAccuracy > fighter2.stats.strikingAccuracy ? "fighter1" : "fighter2",
            unit: "%"
          });
        }
        
        return directStats;
      };
      
      // Get direct stats
      const directStats = extractDirectStats(fighter1.stats, fighter1.name);
      
      // Add direct stats to importantStats
      importantStats = [...directStats, ...importantStats];
      
      // Add odds to important stats if available
      if (fighter1.odds !== undefined && fighter2.odds !== undefined && fighter1.odds !== null && fighter2.odds !== null) {
        // Format odds properly for display
        const formatOdds = (odds: number): string => {
          if (odds > 0) {
            return `+${odds}`;
          } else {
            return `${odds}`;
          }
        };
        
        if (!importantStats.some((stat: {name: string}) => stat.name.toLowerCase().includes('odds'))) {
          importantStats.unshift({
            name: "Betting Odds",
            fighter1Value: formatOdds(fighter1.odds),
            fighter2Value: formatOdds(fighter2.odds),
            advantage: Math.abs(fighter1.odds) < Math.abs(fighter2.odds) ? "fighter1" : "fighter2",
            unit: ""
          });
        }
      }
      
      // Return the formatted fight prediction
      const result = {
        fighter1: {
          name: fighter1.name || "Fighter 1",
          winProbability: fighter1WinProbability,
          record: fighter1Record
        },
        fighter2: {
          name: fighter2.name || "Fighter 2",
          winProbability: fighter2WinProbability,
          record: fighter2Record
        },
        explanation: fight.explanation || "No explanation available",
        narrative: fight.narrative || fight.explanation || "No detailed narrative available",
        importantStats: importantStats,
        statisticalInsights: fight.statistical_insights || []
      };
      
      // Debug logging for odds issues
      console.log(`Fight: ${fighter1.name} vs ${fighter2.name}`);
      console.log(`Original odds: ${fighter1.odds} vs ${fighter2.odds}`);
      console.log(`Narrative length: ${fight.narrative ? fight.narrative.length : 0}`);
      console.log(`ImportantStats odds:`, importantStats.find((s: any) => s.name.includes('Odds')));
      
      return result;
    });
  };
  
  // Load data function
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Loading data...");
      
      console.log("Checking API health...");
      const isApiHealthy = await checkApiHealth();
      console.log("API health check result:", isApiHealthy);
      
      if (isApiHealthy) {
        // First, load the most recent upcoming event
        console.log("Trying to fetch the most recent upcoming event");
        
        try {
          // Get the most recent upcoming event
          const result = await fetchMostRecentUpcomingEvent();
          const apiData = result.data;
          const isUpcoming = result.isUpcoming;
          
          console.log(`Successfully fetched event data: ${apiData.event_name}, isUpcoming: ${isUpcoming}`);
          
          if (!apiData || !apiData.fights || !apiData.fights.length) {
            throw new Error("API returned empty or invalid data");
          }
          
          // Convert to our prediction format and set current fights
          const fights = convertApiDataToFightPredictions(apiData);
          setFightPredictions(fights);
          setEventName(apiData.event_name || "UFC Fight Night");
          setSelectedFightIndex(0);
          
          // Record the event predictions for tracking
          if (apiData.event_name && fights.length > 0) {
            if (isUpcoming) {
              recordEventForTracking(apiData, fights);
            } else {
              recordCompletedEventForTracking(apiData, fights);
            }
          }
          
          // Now fetch past events if they're not already in our history
          const currentHistory = getPredictionHistory();
          if (currentHistory.events.length === 0) {
            console.log("No events in history, fetching past events...");
            
            // Get past events from the API
            const client = clearRequestHeaders();
            const pastEventsResponse = await client.get(`${OFV_API_URL}/api/past_events`);
            const pastEvents = pastEventsResponse.data;
            
            if (pastEvents && Array.isArray(pastEvents)) {
              console.log(`Found ${pastEvents.length} past events to load`);
              
              // Process each past event
              for (const pastEvent of pastEvents) {
                if (pastEvent.event_name && pastEvent.fights) {
                  const pastFights = convertApiDataToFightPredictions(pastEvent);
                  recordCompletedEventForTracking(pastEvent, pastFights);
                  console.log(`Recorded past event: ${pastEvent.event_name}`);
                }
              }
            }
          } else {
            console.log(`Found ${currentHistory.events.length} events in history, skipping past events fetch`);
          }
          
          // Final refresh of prediction history to include all events
          const refreshedHistory = reloadPredictionHistory();
          setPredictionHistory(refreshedHistory);
          
        } catch (apiError) {
          console.error("Error fetching events:", apiError);
          throw apiError;
        }
      } else {
        throw new Error("API health check failed. Cannot load data.");
      }
    } catch (error) {
      console.error('Error loading data:', error);
      
      let errorMessage = 'Failed to load fight data';
      if (error instanceof Error) {
        errorMessage += `: ${error.message}`;
      }
      
      setError(new Error(errorMessage));
      setFightPredictions([]);
      setEventName("Error Loading Data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data and initialize prediction tracking on initial mount
  useEffect(() => {
    // Load fight data
    loadData();
    
    // Check for events needing verification
    const needVerification = findEventsNeedingVerification();
    setEventsNeedingVerification(needVerification.length);
  }, [loadData]);

  // Function to manually refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    setIsToastVisible(true);
    setToastMessage('Loading new data');
    setToastProgress(0);
    setError(null);
    
    try {
      // First check API health
      const isApiHealthy = await checkApiHealth();
      setToastProgress(25);
      
      if (isApiHealthy) {
        // Use the API endpoint to refresh predictions
        const refreshResult = await refreshPredictions();
        setToastProgress(50);
      } else {
        // Fallback to the old method
        const refreshResult = await triggerScraperRefresh();
        setToastProgress(50);
      }
      
      // Wait a moment for the refresh to complete
      await new Promise(resolve => setTimeout(resolve, 3000));
      setToastProgress(75);
      
      // Now reload the data
      await loadData();
      setToastProgress(100);
      
      // Set success message
      setToastMessage('Updated predictions');
      
    } catch (error) {
      console.error('Error refreshing data:', error);
      
      // Handle error
      let errorMessage = 'Failed to refresh data';
      if (error instanceof Error) {
        errorMessage += `: ${error.message}`;
      }
      
      setError(new Error(errorMessage));
      setToastMessage('Failed to refresh data');
      
    } finally {
      setIsRefreshing(false);
    }
  };

  // Add this new method to force a refresh of prediction history
  const forcePredictionHistoryRefresh = () => {
    console.log('Forcing prediction history refresh from storage');
    const refreshedHistory = reloadPredictionHistory();
    setPredictionHistory(refreshedHistory);
    setEventsNeedingVerification(findEventsNeedingVerification().length);
  };

  // Helper function to load past events for the tracker
  const loadPastEventsForTracker = async () => {
    try {
      console.log('Starting loadPastEventsForTracker with complete reset');
      // Do a complete reset of all prediction history
      completeReset();
      
      const client = clearRequestHeaders();
      const response = await client.get(`${OFV_API_URL}/api/predictions`);
      const data = response.data;
      console.log('API Response:', data);
      
      if (!data || !data.fights || !Array.isArray(data.fights) || data.fights.length === 0) {
        console.warn('No predictions available for tracking');
        return;
      }
      
      // Get current date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      console.log('Today:', today);
      
      // Extract date from prediction file if available
      let eventDate = new Date();
      if (data.prediction_file) {
        const dateMatch = data.prediction_file.match(/(\d{8})_/);
        if (dateMatch && dateMatch[1]) {
          const year = parseInt(dateMatch[1].substring(0, 4));
          const month = parseInt(dateMatch[1].substring(4, 6)) - 1; // 0-indexed month
          const day = parseInt(dateMatch[1].substring(6, 8));
          eventDate = new Date(year, month, day);
          console.log('Extracted event date:', eventDate);
        }
      }
      
      // Set hours to 0 for date comparison
      eventDate.setHours(0, 0, 0, 0);
      
      // Determine if this is a past event
      const isPast = eventDate < today;
      console.log('Is past event:', isPast);
      
      if (isPast) {
        console.log('Recording completed event:', data.event_name);
        // Record the event as completed
        await recordEventPredictions(
          data.event_name || 'unknown',
          eventDate.toISOString(),
          data.fights,
          undefined,
          undefined,
          'completed'
        );
      } else {
        console.log('Recording pending event:', data.event_name);
        // Record the event as pending
        await recordEventPredictions(
          data.event_name || 'unknown',
          eventDate.toISOString(),
          data.fights,
          undefined,
          undefined,
          'pending'
        );
      }
      
      // Refresh prediction history
      console.log('Refreshing prediction history');
      await forcePredictionHistoryRefresh();
      console.log('Finished loadPastEventsForTracker');
    } catch (error) {
      console.error('Error loading past events for tracker:', error);
    }
  };

  // Helper to load a specific past event for the tracker
  const loadPastEventForTracker = async (apiData: any) => {
    try {
      const history = getPredictionHistory();
      const hasEvent = history.events.some(
        event => event.eventName === apiData.event_name
      );
      
      if (!hasEvent && apiData.event_name && apiData.fights) {
        console.log("Adding past event to history:", apiData.event_name);
        
        // Convert to our prediction format
        const pastFights = convertApiDataToFightPredictions(apiData);
        
        // Record this past event for tracking
        recordCompletedEventForTracking(apiData, pastFights);
        
        console.log("Recorded past event predictions for tracking");
      } else {
        console.log("Event already exists in history or is invalid");
      }
    } catch (historyError) {
      console.warn("Error checking prediction history:", historyError);
    }
  };

  const value = {
    isLoading,
    isRefreshing,
    refreshStatus,
    error,
    eventName,
    fightPredictions,
    selectedFightIndex,
    predictionHistory,
    eventsNeedingVerification,
    setSelectedFightIndex,
    refreshData,
    setPredictionHistory,
    forcePredictionHistoryRefresh,
    isToastVisible,
    toastProgress,
    toastMessage,
    dismissToast
  };

  return (
    <FightDataContext.Provider value={value}>
      {children}
    </FightDataContext.Provider>
  );
};

// Custom hook to use the fight data context
export const useFightData = (): FightDataContextType => {
  const context = useContext(FightDataContext);
  
  if (context === undefined) {
    throw new Error('useFightData must be used within a FightDataProvider');
  }
  
  return context;
};
